import React, { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  User,
  FileText,
  TrendingUp,
  DollarSign,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  MessageSquare,
} from 'lucide-react';
import { aprobarIngreso, rechazarIngreso } from '../../api/payrollIncomes';
import { personalActionsAPI } from '../../api/personal-actions';
import { absencesAPI } from '../../api/absences';
import { solicitudesAPI } from '../../api/solicitudes';

const TIPO_CONFIG = {
  accion_personal: {
    label: 'Acción de Personal',
    color: 'blue',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    badgeColor: 'bg-blue-100 text-blue-700',
    icon: TrendingUp,
  },
  ingreso_nomina: {
    label: 'Ingreso de Nómina',
    color: 'green',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    badgeColor: 'bg-green-100 text-green-700',
    icon: DollarSign,
  },
  ausencia: {
    label: 'Ausencia',
    color: 'orange',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    badgeColor: 'bg-orange-100 text-orange-700',
    icon: Calendar,
  },
  solicitud: {
    label: 'Solicitud',
    color: 'purple',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    badgeColor: 'bg-purple-100 text-purple-700',
    icon: MessageSquare,
  },
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const s = String(dateStr).split('T')[0];
  return new Date(s + 'T12:00:00').toLocaleDateString('es-DO', {
    year: 'numeric', month: 'short', day: 'numeric'
  });
}

function formatCurrency(amount, moneda = 'DOP') {
  if (!amount && amount !== 0) return '—';
  return new Intl.NumberFormat('es-DO', {
    style: 'currency', currency: moneda, minimumFractionDigits: 2
  }).format(amount);
}

export default function AprobacionCard({ item, onActualizar }) {
  const [expandido, setExpandido] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [mostrarRechazo, setMostrarRechazo] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');

  const config = TIPO_CONFIG[item.tipo] || TIPO_CONFIG.accion_personal;
  const Icon = config.icon;

  const handleAprobar = async () => {
    if (!window.confirm('¿Confirmas la aprobación?')) return;
    setCargando(true);
    try {
      let result;
      if (item.tipo === 'ingreso_nomina') {
        result = await aprobarIngreso(item.id);
      } else if (item.tipo === 'accion_personal') {
        result = await personalActionsAPI.approve(item.id);
      } else if (item.tipo === 'ausencia') {
        result = await absencesAPI.aprobar(item.id);
      } else if (item.tipo === 'solicitud') {
        result = await solicitudesAPI.aprobar(item.id);
      }
      if (result?.estado === 'pendiente') {
        alert('✅ Nivel de aprobación registrado. La solicitud requiere aprobación del siguiente nivel.');
      }
      onActualizar();
    } catch (err) {
      alert('Error al aprobar: ' + (err.response?.data?.error || err.message));
    } finally {
      setCargando(false);
    }
  };

  const handleRechazar = async () => {
    if (!motivoRechazo.trim()) {
      alert('Por favor indica el motivo del rechazo');
      return;
    }
    setCargando(true);
    try {
      if (item.tipo === 'ingreso_nomina') {
        await rechazarIngreso(item.id, motivoRechazo);
      } else if (item.tipo === 'accion_personal') {
        await personalActionsAPI.reject(item.id, motivoRechazo);
      } else if (item.tipo === 'ausencia') {
        await absencesAPI.rechazar(item.id, motivoRechazo);
      } else if (item.tipo === 'solicitud') {
        await solicitudesAPI.rechazar(item.id, { motivo_rechazo: motivoRechazo });
      }
      setMostrarRechazo(false);
      setMotivoRechazo('');
      onActualizar();
    } catch (err) {
      alert('Error al rechazar: ' + (err.response?.data?.error || err.message));
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className={`bg-white rounded-xl border-2 ${config.borderColor} shadow-sm overflow-hidden`}>
      {/* Header */}
      <div className={`p-4 ${config.bgColor}`}>
        <div className="flex items-start gap-3">
          {/* Avatar empleado */}
          <div className="flex-shrink-0">
            {item.empleado_foto ? (
              <img
                src={item.empleado_foto}
                alt={item.empleado_nombre}
                className="w-10 h-10 rounded-full object-cover border-2 border-white shadow"
              />
            ) : (
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow
                ${config.color === 'blue'   ? 'bg-blue-500'   :
                  config.color === 'orange' ? 'bg-orange-500' :
                  config.color === 'purple' ? 'bg-purple-500' : 'bg-green-500'}`}>
                {item.empleado_nombre?.[0]?.toUpperCase() || '?'}
              </div>
            )}
          </div>

          {/* Info principal */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${config.badgeColor}`}>
                <Icon className="w-3 h-3" />
                {config.label}
              </span>
              {item.descripcion_tipo && (
                <span className="text-xs text-gray-500 font-medium">
                  {item.descripcion_tipo}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm font-semibold text-gray-800 truncate">
              {item.empleado_nombre}
              {item.empleado_codigo && (
                <span className="ml-2 text-xs text-gray-400 font-normal">#{item.empleado_codigo}</span>
              )}
            </p>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{item.descripcion}</p>
          </div>

          {/* Fecha */}
          <div className="flex-shrink-0 text-right">
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <Clock className="w-3 h-3" />
              {formatDate(item.fecha)}
            </div>
            {item.solicitado_por && (
              <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                <User className="w-3 h-3" />
                {item.solicitado_por}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detalles expandibles */}
      <div>
        <button
          onClick={() => setExpandido(!expandido)}
          className="w-full flex items-center justify-between px-4 py-2 text-xs text-gray-500 hover:bg-gray-50 transition-colors border-t border-gray-100"
        >
          <span>Ver detalles</span>
          {expandido ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {expandido && (
          <div className="px-4 pb-3 bg-gray-50 border-t border-gray-100">
            {item.tipo === 'ingreso_nomina' && (
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                <div><span className="text-gray-400">Período:</span> <span className="font-medium">{item.periodo} Q{item.quincena}</span></div>
                <div><span className="text-gray-400">Total:</span> <span className="font-semibold text-green-700">{formatCurrency(item.total_ingresos)}</span></div>
                {item.horas_extras > 0 && <div><span className="text-gray-400">Horas extra:</span> <span className="font-medium">{formatCurrency(item.horas_extras)}</span></div>}
                {item.comisiones > 0 && <div><span className="text-gray-400">Comisiones:</span> <span className="font-medium">{formatCurrency(item.comisiones)}</span></div>}
                {item.incentivos > 0 && <div><span className="text-gray-400">Incentivos:</span> <span className="font-medium">{formatCurrency(item.incentivos)}</span></div>}
                {item.bonificaciones > 0 && <div><span className="text-gray-400">Bonificaciones:</span> <span className="font-medium">{formatCurrency(item.bonificaciones)}</span></div>}
                {item.vacaciones > 0 && <div><span className="text-gray-400">Vacaciones:</span> <span className="font-medium">{formatCurrency(item.vacaciones)}</span></div>}
                {item.otros_ingresos > 0 && <div><span className="text-gray-400">Otros:</span> <span className="font-medium">{formatCurrency(item.otros_ingresos)}</span></div>}
              </div>
            )}

            {item.tipo === 'ausencia' && (
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                <div><span className="text-gray-400">Tipo:</span> <span className="font-medium">{item.descripcion_tipo}</span></div>
                <div><span className="text-gray-400">Categoría:</span> <span className="font-medium capitalize">{item.tipo_categoria?.replace(/_/g, ' ')}</span></div>
                <div><span className="text-gray-400">Desde:</span> <span className="font-medium">{formatDate(item.fecha_inicio)}</span></div>
                <div><span className="text-gray-400">Hasta:</span> <span className="font-medium">{formatDate(item.fecha_fin)}</span></div>
                <div className="col-span-2">
                  <span className="text-gray-400">Días solicitados:</span>{' '}
                  <span className="font-semibold text-orange-700">{item.dias_solicitados} días</span>
                  {item.dias_laborables && (
                    <span className="text-gray-500"> ({item.dias_laborables} laborables)</span>
                  )}
                </div>
                {item.fecha_retorno && (
                  <div className="col-span-2">
                    <span className="text-gray-400">Regreso:</span> <span className="font-medium">{formatDate(item.fecha_retorno)}</span>
                  </div>
                )}
                {item.motivo && (
                  <div className="col-span-2">
                    <span className="text-gray-400">Motivo:</span>{' '}
                    <span className="text-gray-700">{item.motivo}</span>
                  </div>
                )}
                {item.tipo_requiere_documento && (
                  <div className="col-span-2">
                    <span className="text-gray-400">Documentos adjuntos:</span>{' '}
                    <span className={`font-medium ${item.total_documentos > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.total_documentos || 0}
                    </span>
                  </div>
                )}
                {item.tipo_flujo && item.nivel_aprobacion_actual !== undefined && (
                  <div className="col-span-2 bg-white rounded p-2 border border-orange-200 mt-2">
                    <p className="text-xs text-orange-700 font-semibold mb-1">Flujo de aprobación:</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.tipo_flujo.map((nivel, idx) => (
                        <React.Fragment key={idx}>
                          <span className={`text-xs px-2 py-1 rounded ${
                            idx < item.nivel_aprobacion_actual ? 'bg-green-100 text-green-700 font-medium' :
                            idx === item.nivel_aprobacion_actual ? 'bg-orange-100 text-orange-700 font-bold ring-2 ring-orange-300' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {nivel}
                          </span>
                          {idx < item.tipo_flujo.length - 1 && <span className="text-gray-400">→</span>}
                        </React.Fragment>
                      ))}
                    </div>
                    {item.aprobaciones && item.aprobaciones.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-gray-500 font-semibold">Historial:</p>
                        {item.aprobaciones.map((apr, idx) => (
                          <div key={idx} className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                            <span className="font-medium">{apr.aprobador_nombre}</span>
                            <span className={`ml-1 ${apr.accion === 'aprobar' ? 'text-green-600' : 'text-red-600'}`}>
                              {apr.accion === 'aprobar' ? 'aprobó' : 'rechazó'}
                            </span>
                            {apr.comentarios && <span className="ml-1 italic">- {apr.comentarios}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {item.tipo === 'solicitud' && (
              <div className="mt-2 text-xs space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {item.tipo_codigo && (
                    <div className="col-span-2">
                      <span className="text-gray-400">Tipo: </span>
                      <span className="font-medium">{item.descripcion_tipo || item.tipo_codigo}</span>
                    </div>
                  )}
                  {item.tipo_categoria && (
                    <div>
                      <span className="text-gray-400">Categoría: </span>
                      <span className="font-medium capitalize">{item.tipo_categoria.replace(/_/g, ' ')}</span>
                    </div>
                  )}
                  {item.prioridad && item.prioridad !== 'normal' && (
                    <div>
                      <span className="text-gray-400">Prioridad: </span>
                      <span className={`font-medium capitalize ${item.prioridad === 'urgente' ? 'text-red-600' : item.prioridad === 'alta' ? 'text-orange-600' : 'text-gray-700'}`}>
                        {item.prioridad}
                      </span>
                    </div>
                  )}
                  {item.fecha_inicio && (
                    <div>
                      <span className="text-gray-400">Desde: </span>
                      <span className="font-medium">{formatDate(item.fecha_inicio)}</span>
                    </div>
                  )}
                  {item.fecha_fin && (
                    <div>
                      <span className="text-gray-400">Hasta: </span>
                      <span className="font-medium">{formatDate(item.fecha_fin)}</span>
                    </div>
                  )}
                  {item.monto_solicitado > 0 && (
                    <div className="col-span-2">
                      <span className="text-gray-400">Monto: </span>
                      <span className="font-semibold text-green-700">{formatCurrency(item.monto_solicitado)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {item.tipo === 'accion_personal' && (() => {
              const raw = item.datos_accion;
              const d = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};
              return (
                <div className="mt-2 text-xs space-y-2">
                  {item.fecha_efectiva && (
                    <div><span className="text-gray-400">Fecha efectiva:</span> <span className="font-medium">{formatDate(item.fecha_efectiva)}</span></div>
                  )}

                  {/* Ajuste salarial */}
                  {item.tipo_accion === 'ajuste_salarial' && (
                    <div className="grid grid-cols-2 gap-2 bg-white rounded-lg p-2 border border-gray-200">
                      <div>
                        <p className="text-gray-400 mb-0.5">Salario anterior</p>
                        <p className="font-semibold text-gray-700">{d.salario_anterior ? formatCurrency(d.salario_anterior, d.moneda_anterior || 'DOP') : '—'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 mb-0.5">Salario nuevo</p>
                        <p className="font-semibold text-green-700">{d.salario_nuevo ? formatCurrency(d.salario_nuevo, d.moneda_nueva || d.moneda_anterior || 'DOP') : '—'}</p>
                      </div>
                      {d.salario_anterior && d.salario_nuevo && (
                        <div className="col-span-2">
                          <span className="text-gray-400">Variación: </span>
                          <span className={`font-semibold ${d.salario_nuevo >= d.salario_anterior ? 'text-green-600' : 'text-red-600'}`}>
                            {d.salario_nuevo >= d.salario_anterior ? '+' : ''}
                            {(((d.salario_nuevo - d.salario_anterior) / d.salario_anterior) * 100).toFixed(1)}%
                          </span>
                        </div>
                      )}
                      {d.tipo_ajuste && (
                        <div className="col-span-2">
                          <span className="text-gray-400">Tipo: </span>
                          <span className="font-medium capitalize">{d.tipo_ajuste.replace(/_/g, ' ')}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Ascenso / Cambio de puesto */}
                  {(item.tipo_accion === 'ascenso' || item.tipo_accion === 'cambio_puesto') && (
                    <div className="grid grid-cols-2 gap-2 bg-white rounded-lg p-2 border border-gray-200">
                      <div>
                        <p className="text-gray-400 mb-0.5">Cargo anterior</p>
                        <p className="font-semibold text-gray-700">{d.cargo_anterior || '—'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 mb-0.5">Cargo nuevo</p>
                        <p className="font-semibold text-blue-700">{d.cargo_nuevo || '—'}</p>
                      </div>
                      {(d.salario_anterior || d.salario_nuevo) && (
                        <>
                          <div>
                            <p className="text-gray-400 mb-0.5">Salario anterior</p>
                            <p className="font-semibold text-gray-700">{d.salario_anterior ? formatCurrency(d.salario_anterior, d.moneda_anterior || 'DOP') : '—'}</p>
                          </div>
                          <div>
                            <p className="text-gray-400 mb-0.5">Salario nuevo</p>
                            <p className="font-semibold text-green-700">{d.salario_nuevo ? formatCurrency(d.salario_nuevo, d.moneda_anterior || 'DOP') : '—'}</p>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Cambio de departamento */}
                  {item.tipo_accion === 'cambio_departamento' && (
                    <div className="grid grid-cols-2 gap-2 bg-white rounded-lg p-2 border border-gray-200">
                      <div>
                        <p className="text-gray-400 mb-0.5">Departamento anterior</p>
                        <p className="font-semibold text-gray-700">{d.departamento_anterior || '—'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 mb-0.5">Departamento nuevo</p>
                        <p className="font-semibold text-blue-700">{d.departamento_nuevo || '—'}</p>
                      </div>
                    </div>
                  )}

                  {/* Cambio de turno */}
                  {item.tipo_accion === 'cambio_turno' && (
                    <div className="grid grid-cols-2 gap-2 bg-white rounded-lg p-2 border border-gray-200">
                      <div>
                        <p className="text-gray-400 mb-0.5">Turno anterior</p>
                        <p className="font-semibold text-gray-700">{d.turno_anterior || '—'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 mb-0.5">Turno nuevo</p>
                        <p className="font-semibold text-blue-700">{d.turno_nuevo || '—'}</p>
                      </div>
                    </div>
                  )}

                  {/* Suspensión */}
                  {item.tipo_accion === 'suspension' && (
                    <div className="bg-white rounded-lg p-2 border border-gray-200 space-y-1">
                      {d.tipo_suspension && <p className="text-xs"><span className="text-gray-400">Tipo: </span><span className="font-medium capitalize">{d.tipo_suspension}</span></p>}
                      {d.dias_suspension && <p className="text-xs"><span className="text-gray-400">Días: </span><span className="font-medium">{d.dias_suspension}</span></p>}
                      {d.goce_salario !== undefined && <p className="text-xs"><span className="text-gray-400">Goce de salario: </span><span className="font-medium">{d.goce_salario ? 'Sí' : 'No'}</span></p>}
                      {d.motivo && <p className="text-xs"><span className="text-gray-400">Motivo: </span><span className="font-medium">{d.motivo}</span></p>}
                    </div>
                  )}

                  {/* Amonestación */}
                  {item.tipo_accion === 'amonestacion' && (
                    <div className="bg-white rounded-lg p-2 border border-gray-200 space-y-1">
                      {d.tipo_amonestacion && <p className="text-xs"><span className="text-gray-400">Tipo: </span><span className="font-medium capitalize">{d.tipo_amonestacion}</span></p>}
                      {d.gravedad && <p className="text-xs"><span className="text-gray-400">Gravedad: </span><span className="font-medium capitalize">{d.gravedad}</span></p>}
                      {d.motivo && <p className="text-xs"><span className="text-gray-400">Motivo: </span><span className="font-medium">{d.motivo}</span></p>}
                    </div>
                  )}

                  {/* Descripción general */}
                  {item.descripcion && (
                    <div><span className="text-gray-400">Descripción:</span> <span className="font-medium">{item.descripcion}</span></div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Acciones: Aprobar / Rechazar */}
      <div className="px-4 pb-4 pt-3 border-t border-gray-100">
        {!mostrarRechazo ? (
          <div className="flex gap-2">
            <button
              onClick={handleAprobar}
              disabled={cargando}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle className="w-4 h-4" />
              Aprobar
            </button>
            <button
              onClick={() => setMostrarRechazo(true)}
              disabled={cargando}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircle className="w-4 h-4" />
              Rechazar
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <textarea
              value={motivoRechazo}
              onChange={e => setMotivoRechazo(e.target.value)}
              placeholder="Motivo del rechazo (requerido)"
              rows={2}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
            />
            <div className="flex gap-2">
              <button
                onClick={handleRechazar}
                disabled={cargando || !motivoRechazo.trim()}
                className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cargando ? 'Procesando...' : 'Confirmar Rechazo'}
              </button>
              <button
                onClick={() => { setMostrarRechazo(false); setMotivoRechazo(''); }}
                className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-semibold rounded-lg transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
